import { FunctionComponent, useState } from 'react'

import styled from 'styled-components'

import Text from '../../../components/Text';

import PlanToggle from '../components/PlanToggle';
import BillingPlans from '../components/BillingPlans';
import CompareFeatures from './CompareFeatures/CompareFeatures';
import { useBillingStore } from '../../../stores/billing/useBillingStore';
import CurrencySelect from '../components/CurrencySelect';
import { IPrice } from '../../../interfaces/models/billingPlan';
import { useAuthStore } from '../../../stores/auth/useAuthStore';
import PlansAndPricingSectionV2 from '../v2/PlansAndPricingSectionV2';
import { isPricingV2 } from '../v2/pricingV2';


interface IPlansAndPricingSectionProps {
    hideTitle?: boolean
}

const PlansAndPricingSection: FunctionComponent<IPlansAndPricingSectionProps> = ({ hideTitle }) => {//move and rename this as a section
    const { store: { billingPlans, selectedBillingPlan, selectedPrice } } = useBillingStore()
    const { store: { subscription, trial }} = useAuthStore()

    const [currency, setCurrency] = useState<string>('usd')
    const [billingPlan, setBillingPlan] = useState({ plan: 'Yearly', key: 'year' })//this is cycle or interval, not plan
    const [showCompareFeatures, setShowCompareFeatures] = useState(false)

   

    const selectedPrices = billingPlans
        .map((plan: any) => {
            // Filter for active prices for the selected interval and currency
            const filteredPrices = plan.prices.filter((price: any) => {
                if (price.interval !== billingPlan.key || price.currency !== currency) return false;
                
                // Check if this is an active price
                const sameProductIntervalPrices = plan.prices.filter((p: any) => 
                    p.interval === price.interval && p.currency === price.currency && p.productId === price.productId
                );
                
                if (sameProductIntervalPrices.length === 1) {
                    // If only one price for this product+interval+currency, keep it (backward compatibility)
                    return true;
                } else {
                    // If multiple prices, only keep active ones
                    return price.active === true;
                }
            });
            
            return {
                name: plan.name,
                prices: filteredPrices
            }
        })
        .filter((plan: any) => (plan.prices && plan.prices.length > 0) || plan.name === 'Enterprise');

    const cantChangePrice = subscription?.isValid && !trial && !(subscription?.cancelDate && subscription?.isValid)

    return (
        <Container>
            <TitleCurrencySection>
                {!hideTitle && <Text $heading1 $bold $black>Start growing replies, reach, and relationships, today.</Text>}
                <StyledCurrencySelect>{!cantChangePrice && <CurrencySelect onChangeHandler={(value) => setCurrency(value)}/>}</StyledCurrencySelect>
                <PlanToggle
                    selectedBillingPlan={selectedBillingPlan}
                    prices={
                        !selectedBillingPlan?.prices
                            ? billingPlans
                                .filter((product: any) => product?.name === 'Business')
                                .flatMap((product: any) => product?.prices)
                                .filter((price: any) => price?.currency === 'usd')
                                .reduce((acc: any[], price: any, _idx: number, arr: any[]) => {
                                    if (price.interval === 'year') {
                                        const yearPrices = arr.filter((p: any) => p.interval === 'year');
                                        if (yearPrices.length > 1) {
                                            if (price.active === true) {
                                                if (!acc.some((p: any) => p.interval === 'year')) acc.push(price);
                                            }
                                        } else {
                                            if (!acc.some((p: any) => p.interval === 'year')) acc.push(price);
                                        }
                                    } else {
                                        acc.push(price);
                                    }
                                    return acc;
                                }, [])
                            : selectedBillingPlan?.prices
                                ?.filter((price: IPrice) => selectedPrice && price.currency === selectedPrice.currency)
                                .reduce((acc: IPrice[], price: IPrice, _idx: number, arr: IPrice[]) => {
                                    if (price.interval === 'year') {
                                        const yearPrices = arr.filter((p: IPrice) => p.interval === 'year');
                                        if (yearPrices.length > 1) {
                                            if (price.active === true) {
                                                if (!acc.some((p: IPrice) => p.interval === 'year')) acc.push(price);
                                            }
                                        } else {
                                            if (!acc.some((p: IPrice) => p.interval === 'year')) acc.push(price);
                                        }
                                    } else {
                                        acc.push(price);
                                    }
                                    return acc;
                                }, [])
                    }
                    selectedInterval={billingPlan}
                    onClickHandler={setBillingPlan}
                /> 
            </TitleCurrencySection>
            <BillingPlans selectedCycle={billingPlan.key} currency={currency}  setShowCompareFeatures={setShowCompareFeatures} />
            {showCompareFeatures && <CompareFeatures selectedPrices={selectedPrices} currency={currency} selectedCycle={billingPlan.key} setShowCompareFeatures={setShowCompareFeatures} />}
        </Container>
    )
}
const Container = styled.div<IPlansAndPricingSectionProps>`
    background-color: white;
    width: 100%;
    padding: 32px 24px 32px 24px;
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    gap: 56px;
    overflow-y: auto;
    height: 100%;
    @media only screen and (min-width: 1024px) {
        gap: 32px;
    }
`
const TitleCurrencySection = styled.section<IPlansAndPricingSectionProps>`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 4px;
    @media only screen and (min-width: 1024px) {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: 40px;
    }
`
const StyledCurrencySelect = styled.div`
    position: absolute;
    right: 0;
    @media only screen and (min-width: 1024px) {
        top: 0
    }

    .actions-dropdown + div {
        min-width: 138px;
    }
`
export default isPricingV2 ? PlansAndPricingSectionV2 : PlansAndPricingSection